import { MAX_MOBILE_WINDOW_WIDTH, ScreenSize, marketplacesWithTransparency } from '../util/constants';
import { translate } from '../i18n';
import { always, getVisibilityFunctions, VisibilityFunctions } from '../util/context-visibility';
import KatalMetricsPublisher from '@amzn/katal-metrics/lib/KatalMetricsPublisher';
import '../styles/navigation.scss';
import { HomepageContext } from '@amzn/brand-context-service-type-script-client';
import { formatMetricString, metricsPublisher } from '../util/metrics';
import { getExperiment } from 'src/util/experiments';

export interface TabItem {
  id: string;
  link: string;
  shownWhen: (visibilityFunctions: VisibilityFunctions) => boolean;
  stringId: string;
}

export interface Tab {
  dropdownOptions: TabItem[];
  id: string;
  stringId: string;
}

const navigationTabs: Tab[] = [
  {
    dropdownOptions: [
      {
        id: 'manage-intellectual-property',
        link: '/manage-ip?ref=brnav_to_managetrademarks',
        shownWhen: ({ customerIs }) => customerIs(['ADMINISTRATOR', 'RIGHTS_OWNER']),
        stringId: 'br-navigation-manage-tab-manage-intellectual-property',
      },
      {
        id: 'manage-selling-accounts',
        link: '/brg/selling-benefits?view=overview',
        shownWhen: ({ customerIs }) => customerIs(['ADMINISTRATOR']),
        stringId: 'br-navigation-manage-tab-manage-selling-accounts',
      },
      {
        id: 'enroll-a-brand',
        link: '/brand/enrollment?ref=brnav_to_enrollbrand',
        shownWhen: always,
        stringId: 'br-navigation-manage-tab-enroll-a-brand',
      },
      {
        id: 'brand-applications',
        link: '/brand/enrollment/manage/applications?ref=brnav_to_brand_applications',
        shownWhen: always,
        stringId: 'br-navigation-manage-tab-brand-applications',
      },
      {
        id: 'manage-house-of-brands',
        link: '/managebrandrelationships/houseofbrands',
        shownWhen: () => getExperiment('BRELUP_HOB_651457') === 'T1',
        stringId: 'br-navigation-manage-tab-manage-house-of-brands',
      },
    ],
    id: 'manage',
    stringId: 'br-navigation-manage-tab',
  },
  {
    dropdownOptions: [
      {
        id: 'project-zero',
        shownWhen: ({ customerHasPzAccess }) => customerHasPzAccess(),
        stringId: 'br-navigation-protect-tab-project-zero',
        link: '/projectzero?ref=brnav_to_projectzero',
      },
      {
        id: 'report-a-violation',
        shownWhen: ({ customerIs }) => customerIs(['REGISTERED_AGENT', 'RIGHTS_OWNER']),
        stringId: 'br-navigation-protect-tab-report-a-violation',
        link: '/brand/report-a-violation?ref=brnav_to_rav',
      },
      {
        id: 'transparency',
        shownWhen: ({ customerIs }) =>
          customerIs(['RIGHTS_OWNER']) && getExperiment('TRANSPARENCY_LINKS_966095') !== 'T1',
        stringId: 'br-navigation-protect-tab-transparency',
        link: 'https://brandservices.amazon.com/transparency/learn-more-br?ref_=tpncy_mw_br_to_bs&ld=IRUSTRLGNGN',
      },
      {
        id: 'transparency-v2',
        shownWhen: ({ customerIs }) =>
          customerIs(['RIGHTS_OWNER']) && getExperiment('TRANSPARENCY_LINKS_966095') === 'T1',
        stringId: 'br-navigation-protect-tab-transparency',
        link: 'https://transparency.amazon.com/v2/tpncyaccount/signup?marketplace=ATVPDKIKX0DER&ref=BRUSTRLGNMN',
      },
    ],
    id: 'protect',
    stringId: 'br-navigation-protect-tab',
  },
  {
    dropdownOptions: [
      {
        id: 'submission-history',
        link: '/brand/submission-history?ref=brnav_to_submissionhistory',
        shownWhen: ({ customerIs }) => customerIs(['REGISTERED_AGENT', 'RIGHTS_OWNER']),
        stringId: 'br-navigation-monitor-tab-submission-history',
      },
      {
        id: 'impact-dashboard',
        link: '/protection/dashboard?ref=brnav_to_impactdashboard',
        shownWhen: ({ customerIs }) => customerIs(['RIGHTS_OWNER']),
        stringId: 'br-navigation-monitor-tab-impact-dashboard',
      },
    ],
    id: 'monitor',
    stringId: 'br-navigation-monitor-tab',
  },
  {
    dropdownOptions: [
      {
        id: 'case-log',
        link: '/gp/case-dashboard/lobby.html?ref=brnav_to_supportcaselog',
        shownWhen: always,
        stringId: 'br-navigation-support-tab-case-log',
      },
      {
        id: 'contact-brand-support',
        link: '/cu/contact-us?ref=brnav_to_contactus',
        shownWhen: ({ customerIs }) => customerIs(['ADMINISTRATOR', 'REGISTERED_AGENT', 'RIGHTS_OWNER']),
        stringId: 'br-navigation-support-tab-contact-brand-support',
      },
      {
        id: 'help-center',
        link: '/gp/help/202130410?ref=brnav_to_helpcenter',
        shownWhen: always,
        stringId: 'br-navigation-support-tab-help-center',
      },
    ],
    id: 'support',
    stringId: 'br-navigation-support-tab',
  },
];

const getTransparencyLink = (countryCode: string) => {
  const path = marketplacesWithTransparency[countryCode]
    ? `marketplace=${marketplacesWithTransparency[countryCode]}&ref=BR${countryCode}TRLGNMN`
    : 'marketplace=ATVPDKIKX0DER&ref=BRUSTRLGNMN';

  return `https://transparency.amazon.com/v2/tpncyaccount/signup?${path}`;
};

let navigationPublisher: KatalMetricsPublisher;

export const renderNavigation = (homepageContext: HomepageContext): HTMLDivElement => {
  navigationPublisher = metricsPublisher.newChildActionPublisherForMethod('Navigation');

  const visibilityFunctions = getVisibilityFunctions(homepageContext);

  let screenSize: ScreenSize = window.innerWidth <= MAX_MOBILE_WINDOW_WIDTH ? 'mobile' : 'normal';

  const navSubMenuSelector = 'brand-registry-mobile-nav-sub-link-container';

  const createMobileNavSubMenuItem = (tab: TabItem): HTMLAnchorElement => {
    const { id, link, stringId } = tab;

    const translatedText = translate(stringId);

    const mobileMenuLink = document.createElement('a');

    const metricString = formatMetricString(id);

    mobileMenuLink.className = 'brand-registry-mobile-nav-menu-item';
    mobileMenuLink.id = `brand-registry-mobile-${id}-tab`;
    mobileMenuLink.text = translatedText;
    mobileMenuLink.href = id === 'transparency-v2' ? getTransparencyLink(homepageContext.countryCode) : link;
    mobileMenuLink.ariaLabel = `brand-registry-mobile-${id}-menu-link`;
    mobileMenuLink.onclick = () => {
      navigationPublisher.publishCounter(`${metricString}MobileLinkClick`, 1);
    };
    return mobileMenuLink;
  };

  const createMobileNavMenuItem = (tab: Tab) => {
    const { dropdownOptions, id, stringId } = tab;

    const outerMenuContainer = document.getElementById('brand-registry-mobile-nav-menu-container');

    const filteredDropdownOptions = dropdownOptions.filter((option) => option.shownWhen(visibilityFunctions));

    if (!filteredDropdownOptions.length) return null;

    if (!outerMenuContainer) {
      throw new Error('Cannot find parent div container');
    }

    const translatedText = translate(stringId);

    const metricString = formatMetricString(id);

    const mobileMenuItem = document.createElement('div');
    mobileMenuItem.className = 'brand-registry-mobile-nav-menu-item';
    mobileMenuItem.id = `brand-registry-mobile-${id}-tab`;
    const mobileMenuLink = document.createElement('a');
    mobileMenuLink.text = translatedText;
    mobileMenuLink.href = '/';
    mobileMenuLink.onclick = (e) => e.preventDefault();
    mobileMenuLink.ariaLabel = `brand-registry-mobile-${id}-nav-menu-item`;
    mobileMenuItem.appendChild(mobileMenuLink);

    mobileMenuItem.onclick = () => {
      const linkContainer = document.getElementById('brand-registry-mobile-nav-links');

      if (!linkContainer) {
        throw new Error('Cannot find parent div container');
      }

      navigationPublisher.publishCounter(`${metricString}MobileMenuItemClick`, 1);

      const newMenu = filteredDropdownOptions.map(createMobileNavSubMenuItem);
      // hide menu level 1 to show sub menu items
      linkContainer.style.display = 'none';

      const backMenuItem = document.createElement('div');
      backMenuItem.className = 'brand-registry-mobile-nav-menu-item';
      backMenuItem.id = 'brand-registry-mobile-back-tab';
      const mobileMenuLink = document.createElement('a');
      mobileMenuLink.text = 'Back';
      mobileMenuLink.className = 'tw-br-trim-ps-4';
      mobileMenuLink.href = '/';
      mobileMenuLink.onclick = (e) => e.preventDefault();
      mobileMenuLink.ariaLabel = 'brand-registry-mobile-nav-menu-back';

      backMenuItem.appendChild(mobileMenuLink);

      const subMenuContainer = document.createElement('div');
      subMenuContainer.className = navSubMenuSelector;
      subMenuContainer.id = navSubMenuSelector;

      subMenuContainer.appendChild(backMenuItem);

      newMenu.forEach((item) => {
        subMenuContainer.appendChild(item);
      });

      outerMenuContainer.appendChild(subMenuContainer);

      // on "Back" click, make menu level 1 visible again and remove sub level
      backMenuItem.onclick = () => {
        outerMenuContainer.removeChild(subMenuContainer);
        linkContainer.style.display = 'inherit';
        navigationPublisher.publishCounter(`${metricString}SubMenuBackBtnClicked`, 1);
      };
    };

    return mobileMenuItem;
  };

  const createMobileNavMenu = () => {
    const mobileDiv = document.createElement('div');
    mobileDiv.id = 'brand-registry-mobile-nav-menu-container';
    const mobileIcon = document.createElement('img');
    const iconImage = document.createElement('a');
    iconImage.className = 'brand-registry-mobile-nav-icon';
    iconImage.href = '/';
    iconImage.onclick = (e) => e.preventDefault();
    iconImage.id = 'brand-registry-mobile-nav-icon';
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    mobileIcon.src = require('../images/menu.svg').default;

    mobileIcon.appendChild(iconImage);
    mobileIcon.className = 'brand-registry-mobile-nav-menu-icon';
    mobileIcon.id = 'brand-registry-mobile-nav-menu-icon';
    mobileIcon.ariaLabel = 'brand-registry-mobile-nav-menu-icon';

    const navSelector = 'brand-registry-mobile-nav-links';

    mobileIcon.onclick = () => {
      const navMenu = document.getElementById(navSelector);
      const navSubMenu = document.getElementById(navSubMenuSelector);

      if (navMenu || navSubMenu) {
        navMenu && mobileDiv.removeChild(navMenu);
        navSubMenu && mobileDiv.removeChild(navSubMenu);
        navigationPublisher.publishCounter('MobileNavMenuClosed', 1);
      } else {
        const mobileNav = document.createElement('div');
        mobileNav.className = navSelector;
        mobileNav.id = navSelector;
        const tabs = navigationTabs.map(createMobileNavMenuItem);
        tabs.forEach((tab) => {
          if (tab) mobileNav.appendChild(tab);
        });
        mobileDiv.appendChild(mobileNav);
        navigationPublisher.publishCounter('MobileNavMenuOpened', 1);
      }
    };

    // if clicked out of, close the menu
    document.addEventListener('click', (e) => {
      const container = document.getElementById('brand-registry-mobile-nav-menu-container');
      const navMenu = document.getElementById(navSelector);
      const navSubMenu = document.getElementById(navSubMenuSelector);

      if (!container) return;

      if (!e.composedPath().includes(container) && navMenu) {
        container.removeChild(navMenu);
        navSubMenu && container.removeChild(navSubMenu);
        navigationPublisher.publishCounter('MobileNavMenuClosedClickOutside', 1);
      }
    });

    mobileDiv.appendChild(mobileIcon);

    return mobileDiv;
  };

  const createNavTab = (tab: Tab) => {
    const { id, stringId, dropdownOptions } = tab;

    const filteredDropdownOptions = dropdownOptions.filter((option) => option.shownWhen(visibilityFunctions));

    if (!filteredDropdownOptions.length) return null;

    const translatedTabName = translate(stringId);
    const metricString = formatMetricString(id);

    const tabContainer = document.createElement('div');
    tabContainer.id = `brand-registry-${id}-tab-container`;
    tabContainer.className = 'brand-registry-nav-tab';

    const navText = document.createElement('a');
    navText.textContent = translatedTabName;
    navText.className = 'brand-registry-nav-tab-link tw-br-trim-flex';
    navText.href = '/';
    navText.ariaLabel = `brand-registry-${id}-nav-tab`;
    navText.id = `brand-registry-${id}-nav-tab-link`;

    const tabPopoverContainer = document.createElement('div');

    const popoverSelector = 'brand-registry-nav-tab-popover-container';

    tabPopoverContainer.className = `${popoverSelector}-closed`;
    tabPopoverContainer.id = `brand-registry-${id}-tab-popover-container`;

    const dropdownIconSelector = 'brand-registry-header-dropdown-icon';
    const dropdownIcon = document.createElement('div');
    dropdownIcon.className = `${dropdownIconSelector}-closed`;
    navText.appendChild(dropdownIcon);

    navText.onclick = (e) => {
      e.preventDefault();

      if (tabPopoverContainer.className === `${popoverSelector}-closed`) {
        tabPopoverContainer.className = `${popoverSelector}-open`;
        dropdownIcon.className = `${dropdownIconSelector}-open`;
        navigationPublisher.publishCounter(`${metricString}TabOpened`, 1);
      } else {
        tabPopoverContainer.className = `${popoverSelector}-closed`;
        dropdownIcon.className = `${dropdownIconSelector}-closed`;
        navigationPublisher.publishCounter(`${metricString}TabClosed`, 1);
      }
    };

    const list = document.createElement('ul');

    filteredDropdownOptions.forEach((option) => {
      const translatedOptionText = translate(option.stringId);

      const optionMetricString = formatMetricString(option.id);

      const subItemLink = document.createElement('a');
      const subItem = document.createElement('li');
      subItemLink.textContent = translatedOptionText;
      subItemLink.href = option.link;
      subItemLink.onclick = () => {
        window.location.href = option.id === 'transparency-v2'
          ? getTransparencyLink(homepageContext.countryCode)
          : option.link;
        navigationPublisher.publishCounter(`${optionMetricString}LinkClicked`, 1);
      };
      subItemLink.id = `brand-registry-${option.id}-nav-tab-link`;
      subItemLink.ariaLabel = `brand-registry-${option.id}-nav-tab-link`;
      subItem.appendChild(subItemLink);
      list.appendChild(subItem);
    });

    tabPopoverContainer.appendChild(list);

    tabContainer.appendChild(navText);
    tabContainer.appendChild(tabPopoverContainer);

    // if clicked out of, close the dropdown
    document.addEventListener('click', (e) => {
      if (
        !e.composedPath().includes(tabContainer) &&
          tabPopoverContainer.className === `${popoverSelector}-open`
      ) {
        tabPopoverContainer.className = `${popoverSelector}-closed`;
        dropdownIcon.className = `${dropdownIconSelector}-closed`;
        navigationPublisher.publishCounter(`${metricString}TabClosedClickOutside`, 1);
      }
    });

    return tabContainer;
  };

  const createFullNavBar = () => {
    const tabContainer = document.createElement('div');
    const tabs = navigationTabs.map(createNavTab);

    tabs.forEach((tab) => {
      if (tab) tabContainer.append(tab);
    });

    return tabContainer;
  };

  const innerComponent: HTMLDivElement = screenSize === 'mobile' ? createMobileNavMenu() : createFullNavBar();
  const selectorName = 'brand-registry-nav-tabs';

  const navigation = document.createElement('div');
  navigation.className = selectorName;
  navigation.id = selectorName;

  window.addEventListener('resize', () => {
    const oldChild = navigation.firstChild;
    if (window.innerWidth <= MAX_MOBILE_WINDOW_WIDTH && screenSize === 'normal' && oldChild) {
      screenSize = 'mobile';
      navigation.replaceChild(createMobileNavMenu(), oldChild);
    } else if (window.innerWidth > MAX_MOBILE_WINDOW_WIDTH && screenSize === 'mobile' && oldChild) {
      screenSize = 'normal';
      navigation.replaceChild(createFullNavBar(), oldChild);
    }
  });

  navigation.append(innerComponent);

  return navigation;
};
